/**
 * Thin fetch wrapper. Same-origin only (cookies are HttpOnly + SameSite=Strict); every unsafe request
 * carries the CSRF token; the device id identifies this browser for offline sync and audit.
 */

import { MSG } from '../content/messages.ts';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    /** Correlation id of the server log entry (x-request-id). */
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Fetch failed before any HTTP response: offline, DNS, server down. */
export class NetworkError extends Error {
  constructor(message: string = MSG.errors.network) {
    super(message);
    this.name = 'NetworkError';
  }
}

let csrfToken = '';
export function setCsrfToken(token: string) {
  csrfToken = token;
}

const DEVICE_KEY = 'ana.deviceId';
export function deviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = `web-${crypto.randomUUID().replace(/-/g, '').slice(0, 24)}`;
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return 'web-ephemeral-device';
  }
}

export function newIdempotencyKey(): string {
  return `idem-${crypto.randomUUID()}`;
}

/**
 * One key per user INTENT, not per click (finance review M1): repeating the same request after a lost
 * response reuses the key, so the server replays the first result instead of creating a duplicate.
 * Call intentDone(signature) after a success to allow a genuinely new identical action later.
 */
const intentKeys = new Map<string, string>();
export function intentKey(signature: string): string {
  let k = intentKeys.get(signature);
  if (!k) {
    k = newIdempotencyKey();
    intentKeys.set(signature, k);
  }
  return k;
}
export function intentDone(signature: string): void {
  intentKeys.delete(signature);
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
export function onUnauthorized(fn: Listener) {
  unauthorizedListeners.add(fn);
  return () => {
    unauthorizedListeners.delete(fn);
  };
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  form?: FormData;
  ifMatch?: number;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

type BootWindow = Window & { __anaSession?: Promise<Response> };

/**
 * The session check started by index.html while the page was parsed (public/session-init.js), handed once to the
 * first GET /auth/me. Later checks (after login, back online) always ask the server again.
 */
export function takeBootSession(): Promise<Response> | undefined {
  const w = window as BootWindow;
  const boot = w.__anaSession;
  delete w.__anaSession;
  return boot;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { 'x-device-id': deviceId() };
  if (method !== 'GET') headers['x-csrf-token'] = csrfToken;
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.ifMatch !== undefined) headers['if-match'] = String(opts.ifMatch);
  if (opts.idempotencyKey) headers['idempotency-key'] = opts.idempotencyKey;
  let res: Response;
  try {
    const boot = method === 'GET' && path === '/auth/me' ? takeBootSession() : undefined;
    res = await (boot ??
      fetch(`/v1${path}`, {
        method,
        headers,
        credentials: 'same-origin',
        body: opts.form ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
        signal: opts.signal,
      }));
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new NetworkError();
  }
  if (res.status === 204) return undefined as T;
  const type = res.headers.get('content-type') ?? '';
  const payload: unknown = type.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const body = payload as {
      error?: { code?: string; message?: string; details?: unknown };
      requestId?: string;
    } | null;
    const err = body?.error;
    if (res.status === 401 && !path.startsWith('/auth/login')) unauthorizedListeners.forEach((l) => l());
    throw new ApiError(
      res.status,
      err?.code ?? `HTTP_${res.status}`,
      err?.message ?? res.statusText,
      err?.details,
      body?.requestId ?? res.headers.get('x-request-id') ?? undefined,
    );
  }
  return payload as T;
}

/** Downloads a server-generated file (CSV export, attachment) through the authenticated session. */
export async function download(path: string, fallbackName: string) {
  const res = await fetch(`/v1${path}`, {
    credentials: 'same-origin',
    headers: { 'x-device-id': deviceId() },
  });
  if (!res.ok) throw new ApiError(res.status, `HTTP_${res.status}`, MSG.errors.download);
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const name = match?.[1] ? decodeURIComponent(match[1]) : fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Message for people. Server business messages are already written in pt-BR and are shown as they are; schema
 * validation details (technical paths, English) and framework messages are replaced by guidance.
 */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'VALIDATION_ERROR') return MSG.errors.validation;
    // A business limit (e.g. INVITE_LIMIT, per hour) explains itself; only the generic per-minute limit gets guidance.
    if (e.code === 'RATE_LIMITED' || (e.status === 429 && !e.message)) return MSG.errors.rateLimited;
    if (e.status >= 500) return MSG.errors.server(e.requestId);
    return e.message || MSG.errors.unexpected;
  }
  if (e instanceof NetworkError) return e.message;
  return MSG.errors.unexpected;
}
